import { NavLink, Outlet, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icon/index.js';
import type { IconName } from '@/components/icon/index.js';
import { cn } from '../lib/utils.js';
import { resolveAvatarSrc } from '../lib/avatar.js';
import { useAuthStore } from '../store/authStore.js';

type SettingsNavItem = { key: string; iconName: IconName; labelKey: string };
type SettingsNavGroup = { labelKey: string; defaultLabel: string; items: SettingsNavItem[] };

const NAV_GROUPS: SettingsNavGroup[] = [
  {
    labelKey: 'settings.groups.account',
    defaultLabel: 'Account',
    items: [
      { key: 'profile', iconName: 'user-2', labelKey: 'settings.tabs.profile' },
      { key: 'security', iconName: 'shield', labelKey: 'settings.tabs.security' },
      { key: 'preferences', iconName: 'settings-2', labelKey: 'settings.tabs.preferences' },
      { key: 'notifications', iconName: 'mail', labelKey: 'settings.tabs.notifications' },
    ],
  },
  {
    labelKey: 'settings.groups.billing',
    defaultLabel: 'Billing',
    items: [
      { key: 'plan', iconName: 'credit-card', labelKey: 'settings.tabs.pack' },
      { key: 'billing', iconName: 'file-text', labelKey: 'settings.tabs.billing' },
      { key: 'promo-codes', iconName: 'gift', labelKey: 'settings.tabs.promoCodes' },
    ],
  },
  {
    labelKey: 'settings.groups.privacy',
    defaultLabel: 'Privacy',
    items: [{ key: 'privacy', iconName: 'scroll-text', labelKey: 'settings.tabs.privacy' }],
  },
];

const ALL_ITEMS = NAV_GROUPS.flatMap((group) => group.items);

function UserCard() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const initials = (user?.name ?? user?.email ?? '?')
    .split(/\s+/)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="relative m-4 overflow-hidden rounded-lg border border-ig-border bg-gradient-to-br from-ig-bg-tertiary to-ig-bg-secondary p-4">
      <div
        aria-hidden
        className="pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full bg-ig-accent opacity-[0.12] blur-2xl"
      />
      <div className="relative flex items-center gap-3">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-ig-accent-border bg-ig-accent-subtle font-sans text-[15px] font-semibold text-ig-accent">
          {user?.avatar ? (
            <img src={resolveAvatarSrc(user.avatar)} alt="" className="h-full w-full object-cover" />
          ) : (
            initials
          )}
        </div>
        <div className="min-w-0">
          <p className="truncate font-sans text-[14px] font-semibold text-ig-text-primary">
            {user?.name ?? t('settings.yourProfile')}
          </p>
          <p className="truncate font-sans text-[12px] text-ig-text-tertiary">{user?.email ?? ''}</p>
        </div>
      </div>
    </div>
  );
}

function NavItemLink({ item, mobile }: { item: SettingsNavItem; mobile: boolean }) {
  const { t } = useTranslation();
  const path = `/settings/${item.key}`;

  if (mobile) {
    return (
      <NavLink
        to={path}
        className={({ isActive }) =>
          cn(
            'inline-flex min-w-max items-center gap-2 rounded-md border px-3.5 py-2 font-sans text-[13px] transition',
            isActive
              ? 'border-ig-accent bg-ig-accent-subtle font-medium text-ig-accent'
              : 'border-ig-border bg-ig-bg-secondary text-ig-text-secondary hover:border-ig-border-strong hover:text-ig-text-primary',
          )
        }
      >
        <Icon name={item.iconName} className="h-4 w-4" />
        <span>{t(item.labelKey)}</span>
      </NavLink>
    );
  }

  return (
    <NavLink
      to={path}
      className={({ isActive }) =>
        cn(
          'group relative flex items-center gap-3 rounded-lg px-3 py-2.5 font-sans text-[14px] transition-all duration-200',
          isActive
            ? 'bg-ig-accent-subtle font-medium text-ig-accent'
            : 'text-ig-text-secondary hover:bg-ig-bg-tertiary hover:text-ig-text-primary',
        )
      }
    >
      {({ isActive }) => (
        <>
          {/* Animated active rail */}
          <span
            aria-hidden
            className={cn(
              'absolute left-0 top-1/2 h-6 w-[3px] -translate-y-1/2 rounded-r-full bg-ig-accent transition-all duration-200',
              isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-40',
            )}
          />
          <span
            className={cn(
              'flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors',
              isActive ? 'bg-ig-accent text-[var(--ig-accent-text)]' : 'bg-ig-bg-tertiary text-ig-text-tertiary group-hover:text-ig-text-secondary',
            )}
          >
            <Icon name={item.iconName} className="h-4 w-4" />
          </span>
          <span>{t(item.labelKey)}</span>
        </>
      )}
    </NavLink>
  );
}

function SettingsNav() {
  const { t } = useTranslation();
  return (
    <nav className="flex flex-col gap-5 px-4 py-2" aria-label={t('settings.title')}>
      {NAV_GROUPS.map((group) => (
        <div key={group.labelKey} className="flex flex-col gap-0.5">
          <p className="mb-1 px-3 font-sans text-[10px] font-semibold uppercase tracking-[0.16em] text-ig-text-tertiary">
            {t(group.labelKey, { defaultValue: group.defaultLabel })}
          </p>
          {group.items.map((item) => (
            <NavItemLink key={item.key} item={item} mobile={false} />
          ))}
        </div>
      ))}
    </nav>
  );
}

export function SettingsLayout() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const activeKey = pathname.split('/')[2] ?? 'profile';
  const activeItem = ALL_ITEMS.find((item) => item.key === activeKey);

  return (
    <div className="mx-auto flex min-h-[calc(100vh-64px)] w-full max-w-[1280px]">
      <aside className="hidden w-[288px] shrink-0 border-r border-ig-border bg-ig-surface-sidebar md:flex md:flex-col">
        <UserCard />
        <div className="mx-4 h-px bg-ig-border" />
        <div className="min-h-0 flex-1 overflow-y-auto">
          <SettingsNav />
        </div>
        <div className="border-t border-ig-border p-4">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 rounded-lg px-3 py-2 font-sans text-[13px] text-ig-text-tertiary transition hover:bg-ig-bg-tertiary hover:text-ig-text-secondary"
          >
            <Icon name="arrow-left" className="h-4 w-4" />
            {t('nav.dashboard')}
          </Link>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Mobile top bar: back link + horizontal nav */}
        <div className="sticky top-0 z-20 border-b border-ig-border bg-ig-bg-primary/90 backdrop-blur-sm md:hidden">
          <div className="flex items-center gap-2 px-4 pt-3">
            <Link to="/dashboard" className="inline-flex items-center gap-1.5 font-sans text-[13px] text-ig-text-tertiary">
              <Icon name="arrow-left" className="h-4 w-4" />
              {t('nav.dashboard')}
            </Link>
            {activeItem ? (
              <span className="ml-auto font-sans text-[13px] font-medium text-ig-text-primary">{t(activeItem.labelKey)}</span>
            ) : null}
          </div>
          <nav className="flex gap-2 overflow-x-auto px-4 py-3" aria-label={t('settings.title')}>
            {ALL_ITEMS.map((item) => (
              <NavItemLink key={item.key} item={item} mobile />
            ))}
          </nav>
        </div>

        <main className="flex min-w-0 flex-1 justify-center bg-ig-bg-primary px-5 py-8 md:px-10 md:py-12 lg:px-14 lg:py-14">
          <div className="w-full max-w-[760px]">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
